import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { getStateCodeFromZipCode } from "@redotech/redo-web/state-code";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { createContext, memo, ReactNode, useState } from "react";
import { z } from "zod";
import { useLocationManager } from "../../location";

const ZIP_STORAGE_KEY = "redo-zip";
const ZIP_EXPIRY_HOURS = 1; // 1 hour expiry

interface ZipCodeContextValue {
  savedZipCode: string | undefined;
  saveZipCode: (zipCode: string) => void;
}

export const ZipCodeContext = createContext<ZipCodeContextValue | null>(null);

export const ZipCodeProvider = memo(function ZipCodeProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const locationManager = useLocationManager();

  const [savedZipCode, setSavedZipCode] = useState<string | undefined>(
    undefined,
  );

  // Save zip code to localStorage and update state
  const saveZipCode = useHandler((zipCode: string) => {
    if (!getStateCodeFromZipCode(zipCode)) {
      throw new Error("Invalid zip code");
    }

    setLocalStorageWithExpiry(
      ZIP_STORAGE_KEY,
      zipCode,
      1000 * 60 * 60 * ZIP_EXPIRY_HOURS,
    );
    setSavedZipCode(zipCode);
  });

  // Load initial zip code from localStorage or location
  useLoad(
    async (signal) => {
      const storedZip = getLocalStorageWithExpiry(ZIP_STORAGE_KEY);
      const zipParsed = z.string().safeParse(storedZip);
      if (zipParsed.success) {
        // We got the zip from localStorage, so we don't need to use the saveZipCode wrapper
        setSavedZipCode(zipParsed.data);
        return;
      }

      const locDetails = await locationManager.getDetailedLocation(signal);
      saveZipCode(locDetails.postalCode);
    },
    [locationManager],
  );

  const zipCodeState: ZipCodeContextValue = { savedZipCode, saveZipCode };

  return (
    <ZipCodeContext.Provider value={zipCodeState}>
      {children}
    </ZipCodeContext.Provider>
  );
});
